import { useCreateUserSubstitutionMutation, useUpdateUserSubstitutionMutation } from 'store/services/core/user';
import { useCallback } from 'react';

import type { SubstitutionFormData, UpdateUserSubstitutionReq, UserSubstitutionItem } from 'types';

const buildUpdatePayload = (
  formData: SubstitutionFormData,
  substitutionId: string,
  previousValues?: UserSubstitutionItem
): UpdateUserSubstitutionReq => {
  const payload: UpdateUserSubstitutionReq = { id: substitutionId };

  if (!previousValues) {
    return payload;
  }

  const newValidFrom = formData.dateFrom?.toISOString();
  const newValidTo = formData.permanentSubstitution ? null : (formData.dateTo?.toISOString() ?? null);

  if (newValidFrom && (!previousValues.validFrom || newValidFrom !== new Date(previousValues?.validFrom).toISOString())) {
    payload.validFrom = newValidFrom;
  }

  // We always send validTo because otherwise, we would set the substitution to permanent if it didn't change
  payload.validTo = newValidTo;

  if (formData.substitutionType && formData.substitutionType !== previousValues.substitutionType) {
    payload.substitutionType = formData.substitutionType;
  }

  const previousRoleIds = previousValues?.roles?.map((role) => role.id) || [];
  const hasRoleChanges =
    formData.selectedRoleIds.length !== previousRoleIds.length || !formData.selectedRoleIds.every((id) => previousRoleIds.includes(id));

  if (hasRoleChanges) {
    payload.roleIds = formData.selectedRoleIds;
  }

  payload.allowNotificationRedirect = formData.allowNotificationRedirect;

  return payload;
};

interface UseSubstitutionSubmissionParams {
  isNew: boolean;
  substitutionId?: string;
  absentUserId?: string;
  previousValues?: UserSubstitutionItem;
}

interface UseSubstitutionSubmissionResult {
  submitSubstitution: (formData: SubstitutionFormData) => Promise<{ error?: string; data?: string }>;
}

export const useSubstitutionSubmission = ({
  isNew,
  substitutionId,
  absentUserId,
  previousValues,
}: UseSubstitutionSubmissionParams): UseSubstitutionSubmissionResult => {
  const [createSubstitution] = useCreateUserSubstitutionMutation();
  const [updateSubstitution] = useUpdateUserSubstitutionMutation();

  const submitSubstitution = useCallback(
    async (formData: SubstitutionFormData): Promise<{ error?: string; data?: string }> => {
      if (!formData.substituteUser) {
        return { error: 'user.substitutes.validation.substituteUser.required' };
      }

      if (!absentUserId) {
        return { error: 'user.substitutes.validation.absentUser.required' };
      }

      if (!formData.dateFrom) {
        return { error: 'user.substitutes.validation.dateFrom.required' };
      }

      if (!formData.substitutionType) {
        return { error: 'user.substitutes.validation.substitutionType.required' };
      }

      if (!formData.permanentSubstitution && !formData.dateTo) {
        return { error: 'user.substitutes.validation.dateTo.required' };
      }

      if (isNew && formData.selectedRoleIds.length === 0) {
        return { error: 'user.substitutes.validation.roles.required' };
      }

      const validTo = formData.permanentSubstitution ? null : (formData.dateTo?.toISOString() ?? null);

      if (isNew) {
        const res = await createSubstitution({
          userId: absentUserId,
          validFrom: formData.dateFrom.toISOString(),
          validTo,
          substitutionType: formData.substitutionType,
          roleIds: formData.selectedRoleIds,
          substitutes: [formData.substituteUser],
          allowNotificationRedirect: formData.allowNotificationRedirect,
        });

        if (res.data) {
          return { data: res.data.userSubstitutionResponses[0]?.id };
        }
        return { error: res.error.toString() };
      } else {
        if (!substitutionId) {
          return { error: 'user.substitutes.validation.substituteUser.required' };
        }

        const payload = buildUpdatePayload(formData, substitutionId, previousValues);

        const res = await updateSubstitution(payload);

        if (res.error) {
          return { error: res.error.toString() };
        }

        return { data: res.data.id };
      }
    },
    [isNew, absentUserId, substitutionId, previousValues, createSubstitution, updateSubstitution]
  );

  return {
    submitSubstitution,
  };
};
